import * as React from 'react';
import { useHotkeys } from '@mantine/hooks';
import { isDefined, isEmpty } from '@nfctron/nexus-core';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { LoaderCircle as LoaderCircleIcon } from 'lucide-react';
import { type VariantProps } from 'tailwind-variants';

import { type ExtendedComponentProps } from '../types';
import { cn, tv } from '../utils';
import { UiTooltip, UiTooltipContent, UiTooltipTrigger } from './tooltip';

/* -------------------------------------------------------------------------------------------------
 * Button
 * -----------------------------------------------------------------------------------------------*/

const buttonVariants = tv({
	base: [
		'inline-flex font-sans items-center whitespace-nowrap rounded-md text-sm font-semibold cursor-pointer ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lucky-point-950 focus-visible:ring-offset-2',
		'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
		'disabled:pointer-events-none disabled:opacity-50',
		'dark:ring-offset-lucky-point-950 dark:focus-visible:ring-lucky-point-300',
	],
	variants: {
		variant: {
			primary: cn('bg-lucky-point-500 text-white hover:opacity-90', 'dark:bg-lucky-point-400 dark:hover:opacity-90'),
			secondary: cn('bg-zinc-100 text-zinc-900 hover:bg-zinc-200', 'dark:bg-zinc-800 dark:text-zinc-50 dark:hover:bg-zinc-700'),
			outlined: cn(
				'border border-zinc-200 text-zinc-900 bg-white hover:bg-zinc-100 hover:text-zinc-900',
				'dark:border-zinc-700 dark:text-zinc-50 dark:bg-zinc-950 dark:hover:bg-zinc-800',
			),
			ghost: cn('hover:bg-zinc-100 text-zinc-900 hover:text-zinc-950', 'dark:hover:bg-zinc-800 dark:text-zinc-50 dark:hover:text-zinc-200'),
			link: cn('text-zinc-900 underline-offset-4 hover:underline', 'dark:text-zinc-50'),
			filled: cn('bg-zinc-500 text-white hover:bg-zinc-500/90', 'dark:bg-zinc-600 dark:hover:bg-zinc-600/90'),
			subtle: cn('bg-zinc-50 text-zinc-500 hover:bg-zinc-100', 'dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'),
			unstyled: '',
		},
		color: {
			'lucky-point': '',
			red: '',
			blue: '',
			orange: '',
			green: '',
			emerald: '',
			unstyled: '',
		},
		state: {
			success: '',
			error: '',
			warning: '',
			info: '',
		},
		size: {
			xs: ['h-6 rounded-base px-2 text-xs gap-1', '[&>.leftSection]:size-3', '[&>.rightSection]:size-3'],
			sm: ['h-8 rounded-md px-3 text-sm gap-1.5', '[&>.leftSection]:size-3.5', '[&>.rightSection]:size-3.5'],
			default: ['h-9 px-4 py-2 gap-2', '[&>.leftSection]:size-4', '[&>.rightSection]:size-4'],
			lg: ['h-11 rounded-md px-8 text-base gap-2.5', '[&>.leftSection]:size-5', '[&>.rightSection]:size-5'],
			icon: 'size-10 [&>.leftSection]:size-4 [&>.rightSection]:size-4',
			unstyled: '',
		},
		align: {
			left: 'justify-start',
			center: 'justify-center',
			right: 'justify-end',
			unstyled: '',
		},
	},
	compoundVariants: [
		/** lucky-point */
		{
			variant: 'filled',
			color: 'lucky-point',
			className: cn('bg-lucky-point-500 text-white hover:opacity-90', 'dark:bg-lucky-point-400 dark:hover:opacity-90'),
		},
		{
			variant: 'outlined',
			color: 'lucky-point',
			className: cn(
				'border-lucky-point-500 text-lucky-point-500 bg-white hover:bg-lucky-point-25 hover:text-lucky-point-700',
				'dark:border-lucky-point-400 dark:text-lucky-point-400 dark:bg-transparent dark:hover:bg-lucky-point-950 dark:hover:text-lucky-point-300',
			),
		},
		{
			variant: 'subtle',
			color: 'lucky-point',
			className: cn(
				'bg-lucky-point-25 text-lucky-point-500 hover:bg-lucky-point-50',
				'dark:bg-lucky-point-950 dark:text-lucky-point-400 dark:hover:bg-lucky-point-900',
			),
		},
		{
			variant: 'ghost',
			color: 'lucky-point',
			className: cn(
				'hover:bg-lucky-point-25 text-lucky-point-500 hover:text-lucky-point-500',
				'dark:hover:bg-lucky-point-950 dark:text-lucky-point-400 dark:hover:text-lucky-point-300',
			),
		},
		{
			variant: 'link',
			color: 'lucky-point',
			className: cn('text-lucky-point-500', 'dark:text-lucky-point-400'),
		},
		/** blue */
		{
			variant: 'primary',
			color: 'blue',
			className: cn('bg-blue-500 text-white hover:bg-blue-500/90', 'dark:bg-blue-400 dark:hover:bg-blue-400/90'),
		},
		{
			variant: 'filled',
			color: 'blue',
			className: cn('bg-blue-500 text-white hover:bg-blue-500/90', 'dark:bg-blue-400 dark:hover:bg-blue-400/90'),
		},
		{
			variant: 'outlined',
			color: 'blue',
			className: cn(
				'border-blue-500 text-blue-500 bg-white hover:bg-blue-50 hover:text-blue-700',
				'dark:border-blue-400 dark:text-blue-400 dark:bg-transparent dark:hover:bg-blue-950 dark:hover:text-blue-300',
			),
		},
		{
			variant: 'subtle',
			color: 'blue',
			className: cn('bg-blue-50 text-blue-500 hover:bg-blue-100', 'dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-900'),
		},
		{
			variant: 'ghost',
			color: 'blue',
			className: cn('hover:bg-blue-50 text-blue-500 hover:text-blue-500', 'dark:hover:bg-blue-950 dark:text-blue-400 dark:hover:text-blue-300'),
		},
		{
			variant: 'link',
			color: 'blue',
			className: cn('text-blue-700', 'dark:text-blue-400'),
		},
		/** red */
		{
			variant: 'primary',
			color: 'red',
			className: cn('bg-red-500 text-white hover:bg-red-500/90', 'dark:bg-red-400 dark:hover:bg-red-400/90'),
		},
		{
			variant: 'filled',
			color: 'red',
			className: cn('bg-red-500 text-white hover:bg-red-500/90', 'dark:bg-red-400 dark:hover:bg-red-400/90'),
		},
		{
			variant: 'outlined',
			color: 'red',
			className: cn(
				'border-red-500 text-red-500 bg-white hover:bg-red-50 hover:text-red-700',
				'dark:border-red-400 dark:text-red-400 dark:bg-transparent dark:hover:bg-red-950 dark:hover:text-red-300',
			),
		},
		{
			variant: 'subtle',
			color: 'red',
			className: cn('bg-red-50 text-red-500 hover:bg-red-100', 'dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900'),
		},
		{
			variant: 'ghost',
			color: 'red',
			className: cn('hover:bg-red-50 text-red-500 hover:text-red-500', 'dark:hover:bg-red-950 dark:text-red-400 dark:hover:text-red-300'),
		},
		{
			variant: 'link',
			color: 'red',
			className: cn('text-red-700', 'dark:text-red-400'),
		},
		/** orange */
		{
			variant: 'primary',
			color: 'orange',
			className: cn('bg-orange-500 text-white hover:bg-orange-500/90', 'dark:bg-orange-400 dark:hover:bg-orange-400/90'),
		},
		{
			variant: 'filled',
			color: 'orange',
			className: cn('bg-orange-500 text-white hover:bg-orange-500/90', 'dark:bg-orange-400 dark:hover:bg-orange-400/90'),
		},
		{
			variant: 'outlined',
			color: 'orange',
			className: cn(
				'border-orange-500 text-orange-500 bg-white hover:bg-orange-50 hover:text-orange-700',
				'dark:border-orange-400 dark:text-orange-400 dark:bg-transparent dark:hover:bg-orange-950 dark:hover:text-orange-300',
			),
		},
		{
			variant: 'subtle',
			color: 'orange',
			className: cn('bg-orange-50 text-orange-500 hover:bg-orange-100', 'dark:bg-orange-950 dark:text-orange-400 dark:hover:bg-orange-900'),
		},
		{
			variant: 'ghost',
			color: 'orange',
			className: cn(
				'hover:bg-orange-50 text-orange-500 hover:text-orange-500',
				'dark:hover:bg-orange-950 dark:text-orange-400 dark:hover:text-orange-300',
			),
		},
		{
			variant: 'link',
			color: 'orange',
			className: cn('text-orange-700', 'dark:text-orange-400'),
		},
		/** green */
		{
			variant: 'primary',
			color: 'green',
			className: cn('bg-emerald-500 text-white hover:bg-emerald-500/90', 'dark:bg-emerald-400 dark:hover:bg-emerald-400/90'),
		},
		{
			variant: 'filled',
			color: 'green',
			className: cn('bg-emerald-500 text-white hover:bg-emerald-500/90', 'dark:bg-emerald-400 dark:hover:bg-emerald-400/90'),
		},
		{
			variant: 'outlined',
			color: 'green',
			className: cn(
				'border-emerald-500 text-emerald-500 bg-white hover:bg-emerald-50 hover:text-emerald-700',
				'dark:border-emerald-400 dark:text-emerald-400 dark:bg-transparent dark:hover:bg-emerald-950 dark:hover:text-emerald-300',
			),
		},
		{
			variant: 'subtle',
			color: 'green',
			className: cn('bg-emerald-50 text-emerald-500 hover:bg-emerald-100', 'dark:bg-emerald-950 dark:text-emerald-400 dark:hover:bg-emerald-900'),
		},
		{
			variant: 'ghost',
			color: 'green',
			className: cn(
				'hover:bg-emerald-50 text-emerald-500 hover:text-emerald-500',
				'dark:hover:bg-emerald-950 dark:text-emerald-400 dark:hover:text-emerald-300',
			),
		},
		{
			variant: 'link',
			color: 'green',
			className: cn('text-emerald-700', 'dark:text-emerald-400'),
		},
	],
	defaultVariants: {
		color: 'unstyled',
		variant: 'primary',
		size: 'default',
		align: 'center',
	},
});

export interface UiButtonProps extends ExtendedComponentProps<VariantProps<typeof buttonVariants>, 'button'> {
	asChild?: boolean;
	/** Makes the button show loading spinner and be disabled when true */
	loading?: boolean;
	/** Left side section content */
	leftSection?: React.ReactNode;
	/** Right side section content */
	rightSection?: React.ReactNode;
	/** Tooltip content of the button, uses `UiTooltip` */
	tooltip?: React.ReactNode;
	/** Makes the button grow to the full width of its container */
	fullWidth?: boolean;
	/** @see useHotkeys from @mantine/hooks */
	hotkey?: string | string[];
	/** Button content / children */
	children?: React.ReactNode;
}

const UiButton = React.forwardRef<HTMLButtonElement, UiButtonProps>((props, ref) => {
	const {
		children,
		className,
		variant,
		size,
		color,
		asChild = false,
		rightSection,
		align,
		loading,
		leftSection: leftSectionProp,
		tooltip,
		hotkey,
		fullWidth = false,
		...rest
	} = props;
	const Comp = asChild ? Slot : 'button';
	const leftSection = loading ? <LoaderCircleIcon className="animate-spin" /> : leftSectionProp;

	/** hotkeys support */
	const hotkeys = typeof hotkey === 'string' ? [hotkey] : hotkey ?? [];
	useHotkeys(
		hotkeys.map((key) => [
			key,
			(e) => {
				e.preventDefault();
				e.stopPropagation();
				if (!props.loading && !props.disabled) props.onClick?.(e as any);
			},
		]),
		/** do not ignore hotkeys events on any element */
		[],
	);

	const buttonNode = (
		<Comp
			data-disabled={props.disabled || loading}
			type="button"
			className={cn(
				buttonVariants({
					variant,
					size,
					className,
					align,
					color,
				}),
				fullWidth && 'w-full',
			)}
			ref={ref}
			{...rest}
		>
			{/* left section */}
			{isDefined(leftSection) && <Slot className="leftSection shrink-0">{leftSection}</Slot>}
			{/* slottable children */}
			<Slottable>{children}</Slottable>
			{/* right section */}
			{isDefined(rightSection) && <Slot className="rightSection shrink-0">{rightSection}</Slot>}
		</Comp>
	);

	if (!isEmpty(tooltip)) {
		return (
			<UiTooltip>
				<UiTooltipTrigger asChild>{buttonNode}</UiTooltipTrigger>
				<UiTooltipContent>{tooltip}</UiTooltipContent>
			</UiTooltip>
		);
	}

	return buttonNode;
});
UiButton.displayName = 'ui/Button';

/* -----------------------------------------------------------------------------------------------*/

export { UiButton, buttonVariants };
